import { useState, useEffect, useCallback, useRef } from 'react';
import { getQuizWithQuestions, getQuiz } from '../../services/studentQuizService';
import { getStoredSession, storeSession, clearSession } from '../../pages/Quiz/utils/quizSessionStorage';
import { usePremiumModal } from '../../components/premium-modal/PremiumModalProvider';
import { toast } from 'sonner';

export default function useQuizSetup(quiz_id, onModalClose) {
  const { showPremiumModal, showUpgradeModal } = usePremiumModal();
  const onModalCloseRef = useRef(onModalClose);
  const hasStartedRef = useRef(null);

  useEffect(() => {
    onModalCloseRef.current = onModalClose;
  }, [onModalClose]);

  const [quiz, setQuiz] = useState(null);
  const [session, setSession] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [verificationState, setVerificationState] = useState(null);
  const [showSetupFlow, setShowSetupFlow] = useState(false);
  const [calibratedBaselines, setCalibratedBaselines] = useState(null);
  const [sessionInfo, setSessionInfo] = useState(() => getStoredSession(quiz_id));

  const _setSessionInfo = useCallback((qId, sessionId, startedAt, enableCalculator = false) => {
    const info = { sessionId, startedAt, enable_calculator: Boolean(enableCalculator) };
    setSessionInfo(info);
    storeSession(qId, sessionId, startedAt, { enable_calculator: Boolean(enableCalculator) });
  }, []);

  // Flow for starting a brand new quiz session (checks for proctoring/verification setup)
  const startNewSession = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const basicQuiz = await getQuiz(quiz_id);
      setQuiz(basicQuiz);

      const needsVerification = basicQuiz.enforce_id_verification;
      const needsProctoring = basicQuiz.proctoring_enabled;
      const needsEnvironmentCheck = basicQuiz.environment_check_enabled;
      const needsInstructions = basicQuiz.quiz_type === "exam";

      if (needsVerification || needsProctoring || needsEnvironmentCheck || needsInstructions) {
        setShowSetupFlow(true);
      } else {
        // No setup needed, start fresh session immediately
        const { session: sess, quiz: quizData, questions: questionData } = await getQuizWithQuestions(quiz_id, null);
        setQuiz(quizData);
        setSession(sess);
        setQuestions(questionData);
        const isCalcEnabled = Boolean(sess?.enable_calculator ?? quizData?.enable_calculator);
        _setSessionInfo(quiz_id, sess.id, sess.started_at, isCalcEnabled);
        setVerificationState(null);
      }
    } catch (err) {
      console.error('Error starting new quiz session:', err);
      const errorObj = err?.response?.data;
      const errorMsg = errorObj?.message || errorObj?.detail || errorObj?.error || 'Failed to start quiz setup.';
      setError(errorMsg);
      toast.error(errorMsg);
      if (errorObj?.code === 'UPGRADE_REQUIRED') showUpgradeModal('retakes');
      else showPremiumModal(errorMsg, onModalCloseRef.current);
    } finally {
      setLoading(false);
    }
  }, [quiz_id, _setSessionInfo, showPremiumModal, showUpgradeModal]);

  const initializeQuiz = useCallback(async (forceNew = false) => {
    const existingSession = forceNew ? null : getStoredSession(quiz_id);

    try {
      setLoading(true);
      setError(null);

      const { session: sess, quiz: quizData, questions: questionData } = await getQuizWithQuestions(
        quiz_id,
        existingSession?.sessionId
      );
      setQuiz(quizData);
      setSession(sess);
      setQuestions(questionData);
      const isCalcEnabled = Boolean(
        sess?.enable_calculator ?? quizData?.enable_calculator ?? existingSession?.enable_calculator
      );
      _setSessionInfo(quiz_id, sess.id, sess.started_at, isCalcEnabled);
      setVerificationState(null);

      if (questionData.length === 0) {
        return;
      }
    } catch (err) {
      console.error('Error initializing quiz:', err);

      const isNotFound =
        err?.response?.status === 404 ||
        err?.status === 404 ||
        err?.response?.data?.detail?.toString()?.toLowerCase()?.includes('not found');

      // If we attempted to resume a stored session and got 404 Not Found,
      // the session is expired or invalid on the backend.
      // Clear the stale session and automatically start a fresh session without showing an error.
      if (existingSession?.sessionId && isNotFound) {
        console.warn(
          `Stored session ${existingSession.sessionId} returned 404 Not Found. Clearing stale session and starting a fresh session.`
        );
        clearSession(quiz_id, existingSession.sessionId);
        setSessionInfo(null);
        await startNewSession();
        return;
      }

      const errorObj = err?.response?.data;
      const errorCode = errorObj?.code;

      if (
        errorCode === 'id_upload_required' ||
        errorCode === 'verification_required' ||
        errorCode === 'environment_verification_required'
      ) {
        if (errorCode === 'environment_verification_required') {
          setShowSetupFlow(true);
        } else {
          setVerificationState(
            errorCode === 'id_upload_required' ? 'id_upload' : 'webcam_verification'
          );
        }
        try {
          const basicQuiz = await getQuiz(quiz_id);
          setQuiz(basicQuiz);
        } catch (quizErr) {
          console.error("Failed to load basic quiz info:", quizErr);
        }
      } else {
        const errorMsg =
          errorObj?.message || errorObj?.detail || errorObj?.error || 'Failed to start quiz.';
        setError(errorMsg);
        toast.error(errorMsg);
        if (errorCode === 'UPGRADE_REQUIRED') showUpgradeModal('retakes');
        else showPremiumModal(errorMsg, onModalCloseRef.current);
      }
    } finally {
      setLoading(false);
    }
  }, [quiz_id, showPremiumModal, showUpgradeModal, _setSessionInfo, startNewSession]);

  // Check if we need setup/calibration before starting the quiz
  useEffect(() => {
    if (!quiz_id || hasStartedRef.current === quiz_id) return;
    hasStartedRef.current = quiz_id;

    const checkSetupAndStart = async () => {
      const existingSession = getStoredSession(quiz_id);

      // Case A: Resuming an existing session - bypass setup flow and initialize quiz directly
      if (existingSession?.sessionId) {
        await initializeQuiz();
        return;
      }

      // Case B: Starting a new session - run setup flow check
      await startNewSession();
    };

    checkSetupAndStart();
  }, [quiz_id, initializeQuiz, startNewSession]);

  const handleSetupComplete = async ({ baselines }) => {
    if (baselines) {
      setCalibratedBaselines(baselines);
    }
    setShowSetupFlow(false);
    await initializeQuiz(true);
  };

  return {
    quiz,
    session,
    questions,
    loading,
    error,
    verificationState,
    setVerificationState,
    showSetupFlow,
    calibratedBaselines,
    sessionInfo,
    handleSetupComplete,
    initializeQuiz
  };
}
